/**
 * Achievements.
 *
 * Each one is a pure predicate over a profile, so the set can be re-evaluated
 * at any moment (after a lesson, on a page load, after a data import) and will
 * always agree with the underlying progress. There is no "award" event to miss
 * and no state to get out of sync.
 *
 * Deliberate design choice: nothing here rewards raw speed alone. Every badge
 * is earned through consistency, accuracy, curiosity or completion — the
 * behaviours that actually make a fast typist.
 */
import type { AchievementDef, KidProfile } from '../types';
import { ALL_LESSONS, LEVELS } from './curriculum';
import { completedCount, totalStars } from '../lib/progress';
import { isQualifyingRun } from '../lib/metrics';

const lessonsIn = (levelId: string) => ALL_LESSONS.filter((l) => l.levelId === levelId);

const levelComplete = (p: KidProfile, levelId: string) =>
  lessonsIn(levelId).every((l) => p.lessons[l.id]?.completed);

/**
 * Speed badges only ever count *qualifying* runs — a long enough exercise for
 * the number to mean something. Without this, a child (or a stuck key) can
 * unlock every speed badge on the 63-character Level 1 warm-up, which both
 * cheapens the badges and hands out the whole coin economy on lesson one.
 */
const bestWpm = (p: KidProfile) =>
  p.sessions.filter(isQualifyingRun).reduce((max, s) => Math.max(max, s.wpm), 0);

const perfectRuns = (p: KidProfile) => p.sessions.filter((s) => s.accuracy === 100).length;

const distinctDays = (p: KidProfile) => new Set(p.sessions.map((s) => s.day)).size;

const totalMinutes = (p: KidProfile) =>
  Math.round(p.sessions.reduce((sum, s) => sum + s.seconds, 0) / 60);

const ratio = (value: number, target: number) => Math.max(0, Math.min(1, value / target));

export const ACHIEVEMENTS: AchievementDef[] = [
  /* ── first steps ─────────────────────────────────────────────────────── */
  {
    id: 'first-keystroke',
    name: 'First Keystroke',
    description: 'Finish your very first lesson.',
    emoji: '🐣',
    tier: 'bronze',
    coins: 10,
    test: (p) => completedCount(p) >= 1,
    progress: (p) => ratio(completedCount(p), 1),
  },
  {
    id: 'home-base',
    name: 'Home Base',
    description: 'Complete every lesson in Home Base.',
    emoji: '🏠',
    tier: 'bronze',
    coins: 15,
    test: (p) => levelComplete(p, 'l01'),
  },
  {
    id: 'full-home-row',
    name: 'Eight Fingers, Eight Keys',
    description: 'Finish the whole home row (Levels 1–4).',
    emoji: '🖐️',
    tier: 'silver',
    coins: 30,
    test: (p) => ['l01', 'l02', 'l03', 'l04'].every((id) => levelComplete(p, id)),
    progress: (p) =>
      ratio(['l01', 'l02', 'l03', 'l04'].filter((id) => levelComplete(p, id)).length, 4),
  },
  {
    id: 'first-words',
    name: 'Real Words',
    description: 'Type your first real words in Level 6.',
    emoji: '📖',
    tier: 'bronze',
    coins: 15,
    test: (p) => levelComplete(p, 'l06'),
  },
  {
    id: 'alphabet',
    name: 'A to Z',
    description: 'Learn every letter of the alphabet.',
    emoji: '🔤',
    tier: 'gold',
    coins: 50,
    test: (p) => levelComplete(p, 'l08'),
  },

  /* ── accuracy ────────────────────────────────────────────────────────── */
  {
    id: 'flawless',
    name: 'Flawless',
    description: 'Finish a lesson with 100% accuracy.',
    emoji: '💎',
    tier: 'silver',
    coins: 25,
    test: (p) => perfectRuns(p) >= 1,
  },
  {
    id: 'flawless-ten',
    name: 'Diamond Fingers',
    description: 'Finish 10 lessons with 100% accuracy.',
    emoji: '💠',
    tier: 'gold',
    coins: 60,
    test: (p) => perfectRuns(p) >= 10,
    progress: (p) => ratio(perfectRuns(p), 10),
  },
  {
    id: 'sharpshooter',
    name: 'Sharpshooter',
    description: 'Keep 97% accuracy or better across 5 lessons in a row.',
    emoji: '🎯',
    tier: 'silver',
    coins: 30,
    test: (p) => {
      const last = p.sessions.slice(-5);
      return last.length === 5 && last.every((s) => s.accuracy >= 97);
    },
  },

  /* ── consistency ─────────────────────────────────────────────────────── */
  {
    id: 'streak-3',
    name: 'Three in a Row',
    description: 'Practise three days in a row.',
    emoji: '🔥',
    tier: 'bronze',
    coins: 15,
    test: (p) => p.streak.longest >= 3,
    progress: (p) => ratio(p.streak.longest, 3),
  },
  {
    id: 'streak-7',
    name: 'Week Warrior',
    description: 'Practise seven days in a row.',
    emoji: '🗓️',
    tier: 'silver',
    coins: 40,
    test: (p) => p.streak.longest >= 7,
    progress: (p) => ratio(p.streak.longest, 7),
  },
  {
    id: 'streak-30',
    name: 'Unstoppable',
    description: 'Practise thirty days in a row.',
    emoji: '🌟',
    tier: 'legend',
    coins: 150,
    test: (p) => p.streak.longest >= 30,
    progress: (p) => ratio(p.streak.longest, 30),
  },
  {
    id: 'twenty-days',
    name: 'Regular',
    description: 'Practise on twenty different days.',
    emoji: '📅',
    tier: 'silver',
    coins: 35,
    test: (p) => distinctDays(p) >= 20,
    progress: (p) => ratio(distinctDays(p), 20),
  },
  {
    id: 'hour-club',
    name: 'The Hour Club',
    description: 'Spend sixty minutes practising in total.',
    emoji: '⏳',
    tier: 'silver',
    coins: 35,
    test: (p) => totalMinutes(p) >= 60,
    progress: (p) => ratio(totalMinutes(p), 60),
  },
  {
    id: 'ten-hours',
    name: 'Dedicated',
    description: 'Spend ten hours practising in total.',
    emoji: '🕰️',
    tier: 'legend',
    coins: 120,
    test: (p) => totalMinutes(p) >= 600,
    progress: (p) => ratio(totalMinutes(p), 600),
  },

  /* ── speed (always paired with accuracy in the star rules) ───────────── */
  {
    id: 'wpm-15',
    name: 'Getting Quick',
    description: 'Reach 15 words per minute.',
    emoji: '🚶',
    tier: 'bronze',
    coins: 15,
    test: (p) => bestWpm(p) >= 15,
    progress: (p) => ratio(bestWpm(p), 15),
  },
  {
    id: 'wpm-25',
    name: 'Speedy',
    description: 'Reach 25 words per minute.',
    emoji: '🏃',
    tier: 'silver',
    coins: 30,
    test: (p) => bestWpm(p) >= 25,
    progress: (p) => ratio(bestWpm(p), 25),
  },
  {
    id: 'wpm-40',
    name: 'Lightning Hands',
    description: 'Reach 40 words per minute.',
    emoji: '⚡',
    tier: 'gold',
    coins: 70,
    test: (p) => bestWpm(p) >= 40,
    progress: (p) => ratio(bestWpm(p), 40),
  },
  {
    id: 'wpm-60',
    name: 'Supersonic',
    description: 'Reach 60 words per minute.',
    emoji: '🚀',
    tier: 'legend',
    coins: 150,
    test: (p) => bestWpm(p) >= 60,
    progress: (p) => ratio(bestWpm(p), 60),
  },

  /* ── collection ──────────────────────────────────────────────────────── */
  {
    id: 'stars-30',
    name: 'Star Collector',
    description: 'Collect 30 stars.',
    emoji: '⭐',
    tier: 'bronze',
    coins: 20,
    test: (p) => totalStars(p) >= 30,
    progress: (p) => ratio(totalStars(p), 30),
  },
  {
    id: 'stars-100',
    name: 'Constellation',
    description: 'Collect 100 stars.',
    emoji: '✨',
    tier: 'gold',
    coins: 80,
    test: (p) => totalStars(p) >= 100,
    progress: (p) => ratio(totalStars(p), 100),
  },
  {
    id: 'perfect-level',
    name: 'Three Star Level',
    description: 'Earn three stars on every lesson of any level.',
    emoji: '🏅',
    tier: 'gold',
    coins: 60,
    test: (p) => LEVELS.some((lv) => lv.lessons.every((l) => p.lessons[l.id]?.stars === 3)),
  },
  {
    id: 'halfway',
    name: 'Halfway Hero',
    description: 'Complete half of the journey.',
    emoji: '🧗',
    tier: 'silver',
    coins: 40,
    test: (p) => completedCount(p) >= Math.ceil(ALL_LESSONS.length / 2),
    progress: (p) => ratio(completedCount(p), Math.ceil(ALL_LESSONS.length / 2)),
  },
  {
    id: 'master-typist',
    name: 'Master Typist',
    description: 'Complete all 15 levels of the journey.',
    emoji: '👑',
    tier: 'legend',
    coins: 250,
    test: (p) => completedCount(p) >= ALL_LESSONS.length,
    progress: (p) => ratio(completedCount(p), ALL_LESSONS.length),
  },

  /* ── curiosity ───────────────────────────────────────────────────────── */
  {
    id: 'storyteller',
    name: 'Storyteller',
    description: 'Type all three stories in Story Forest.',
    emoji: '📚',
    tier: 'gold',
    coins: 55,
    test: (p) => levelComplete(p, 'l13'),
    progress: (p) => ratio(lessonsIn('l13').filter((l) => p.lessons[l.id]?.completed).length, 3),
  },
  {
    id: 'dressed-up',
    name: 'Looking Sharp',
    description: 'Unlock five items in the reward shop.',
    emoji: '🎩',
    tier: 'bronze',
    coins: 20,
    test: (p) => p.unlocked.length >= 5,
    progress: (p) => ratio(p.unlocked.length, 5),
  },
  {
    id: 'comeback',
    name: 'Comeback Kid',
    description: 'Improve your stars on a lesson you already finished.',
    emoji: '🔁',
    tier: 'bronze',
    coins: 15,
    test: (p) => Object.values(p.lessons).some((r) => r.attempts >= 2 && r.stars >= 2),
  },
];

const BY_ID = new Map(ACHIEVEMENTS.map((a) => [a.id, a]));
export const getAchievement = (id: string) => BY_ID.get(id) ?? null;

/**
 * Re-evaluate every achievement and return the ids newly earned.
 * Pure: the caller decides what to do with the result.
 */
export function evaluateAchievements(profile: KidProfile): string[] {
  return ACHIEVEMENTS.filter((a) => !profile.achievements[a.id] && a.test(profile)).map(
    (a) => a.id
  );
}

export const TIER_ORDER: Record<string, number> = { bronze: 0, silver: 1, gold: 2, legend: 3 };
